import { emailTemplate } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Email Template Service
 * Handles all email template-related API calls.
 * 
 * API Endpoints:
 * - GET /api/v1/admin/email-template/list - Get email template list
 * - GET /api/v1/admin/email-template/get - Get single email template
 * - POST /api/v1/admin/email-template/add - Add new email template
 * - PUT /api/v1/admin/email-template/update - Update email template
 * - DELETE /api/v1/admin/email-template/delete - Delete email template
 */
class EmailTemplateService {
  /**
   * @name getList
   * @description Fetches email template list from admin API with pagination and search.
   * @param {Object} params - Query parameters (page, limit, search_text)
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing the email template list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(emailTemplate.list, { params });
      return response.data;
    } catch (err) {
      throw err; // Re-throw the error for the caller to handle
    }
  }

  /**
   * @name get
   * @description Fetches a single email template by UUID.
   * @param {string} uuid - Email Template UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing email template details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(emailTemplate.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new email template.
   * @param {Object} data - Email template data with slug, parameter, and templates array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(emailTemplate.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing email template.
   * @param {Object} data - Email template data with uuid, slug, parameter, and templates array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(emailTemplate.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes an email template by UUID.
   * @param {string} uuid - Email Template UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(emailTemplate.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { EmailTemplateService };
